type EngineInput = {
  username: string;
  followers: number;
  percent: number;
  engineVersion?: number;
};

type OperatorConfig = {
  prefixes: string[];
  weight: number;
};

export class InstagramMobileDatasetEngine {

  private targetCount: number;
  private producedPhones = new Set<string>();
  private allPhones: string[] = [];

  private prngSeed: number;

  private operators: OperatorConfig[] = [
    { prefixes: ['0901', '0902', '0903', '0904', '0905', '0930', '0933', '0935', '0936', '0937', '0938', '0939', '0941'], weight: 45 },
    { prefixes: ['0910', '0911', '0912', '0913', '0914', '0915', '0916', '0917', '0918', '0919', '0990', '0991', '0992'], weight: 40 },
    { prefixes: ['0920', '0921', '0922'], weight: 6 },
    { prefixes: ['0932'], weight: 3 },
    { prefixes: ['0931', '0934'], weight: 2 },
    { prefixes: ['099810', '099811', '099812'], weight: 2 },
    { prefixes: ['099910', '099911', '099913', '099914'], weight: 1 },
    { prefixes: ['099996', '099997', '099998', '099999', '09990', '09944'], weight: 1 }
  ];

  constructor(input: EngineInput) {
    const version = input.engineVersion ?? 2;
    const key = `${version}|${input.username}|${input.followers}|${input.percent}`;
    this.prngSeed = this.hash64(key);

    this.targetCount = Math.floor(input.followers * (input.percent / 100));
    this.generateAllPhones();
    this.shufflePhones();
  }

  /* ================= PUBLIC API ================= */

  next(): string | null {
    if (this.allPhones.length === 0) return null;
    const phone = this.allPhones.shift()!;
    this.producedPhones.add(phone);
    return phone;
  }

  generateAll(): string[] {
    return [...this.allPhones];
  }

  async *stream(): AsyncGenerator<string> {
    while (this.allPhones.length > 0) {
      yield this.next()!;
      await Promise.resolve();
    }
  }

  /* ================= PHONE GENERATION ================= */

  private generateAllPhones() {
    while (this.allPhones.length < this.targetCount) {
      let phone: string;
      let attempts = 0;
      do {
        if (attempts++ > 1000) break;
        phone = this.generatePhone();
      } while (this.producedPhones.has(phone));
      this.producedPhones.add(phone);
      this.allPhones.push(phone);
    }
  }

  private generatePhone_(): string {
    const prefix = this.pickPrefix();
    const restLen = 11 - prefix.length;

    let rest: string;
    do {
      const rand = Math.floor(this.prng() * Math.pow(10, restLen));
      rest = rand.toString().padStart(restLen, '0');
    } while (this.isBlacklisted(rest));

    return prefix + rest;
  }

  private generatePhone(): string {

    const prefix = this.pickPrefix();
    let abc: string;
    
    do {
      abc = Math.floor(this.prng() * 1000).toString().padStart(3, '0');
    } while (this.isBlacklistedABC(abc));

    let defg: string;
    do {
      defg = Math.floor(this.prng() * 10000).toString().padStart(4, '0');
    } while (this.isBlacklisted(defg));
    return prefix + abc + defg;
  }

  private pickPrefix(): string {
    const totalWeight = this.operators.reduce((sum, op) => sum + op.weight, 0);
    let r = this.prng() * totalWeight;
    for (const op of this.operators) {
      if (r < op.weight) {
        const i = Math.floor(this.prng() * op.prefixes.length);
        return op.prefixes[i];
      }
      r -= op.weight;
    }
    return this.operators[0].prefixes[0];
  }

  private shufflePhones() {
    // Fisher-Yates shuffle
    for (let i = this.allPhones.length - 1; i > 0; i--) {
      const j = Math.floor(this.prng() * (i + 1));
      [this.allPhones[i], this.allPhones[j]] = [this.allPhones[j], this.allPhones[i]];
    }
  }

  /* ================= HUMAN-LIKE DIGITS ================= */

  private isBlacklisted(s: string): boolean {
    if (/(\d)\1{3}/.test(s)) return true;
    if (/0123|1234|2345|3456|4567|5678|6789/.test(s)) return true;
    if (/9876|8765|7654|6543|5432|4321|3210/.test(s)) return true;
    if (/0000$/.test(s)) return true;
    if (/555/.test(s)) return true;
    return false;
  }

  private isBlacklistedABC(s: string): boolean {
    if (/(\d)\1{2}/.test(s)) return true;
    if (/012|123|234|345|456|567|678|789/.test(s)) return true;
    if (/987|876|765|654|543|432|321|210/.test(s)) return true;
    if (s === '000' || s === '555') return true;
    return false;
  }

  private fixHuman(s: string): string {
    return s.replace(/(\d)\1{2,}/g, (_, d) => d + ((+d + 3) % 10));
  }

  /* ================= PRNG ================= */

  private prng(): number {
    let t = this.prngSeed += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  private hash64(str: string): number {
    let h1 = 0xdeadbeef ^ str.length;
    let h2 = 0x41c6ce57 ^ str.length;
    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h2 = Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    return Math.abs((h2 >>> 0) * 4294967296 + (h1 >>> 0));
  }
}